import React, { useState, useEffect, useRef } from 'react'
import {
  Users,
  Plus,
  Search,
  Edit2,
  Trash2,
  Eye,
  Upload,
  Download,
  CheckCircle,
  XCircle,
  Shield,
  Mail,
  FileSpreadsheet
} from 'lucide-react'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import Modal from '../../components/common/Modal'
import Table from '../../components/common/Table'
import Card from '../../components/common/Card'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { userService } from '../../services/userService'
import { roleService } from '../../services/roleService'
import { useAuth } from '../../contexts/AuthContext'
import { exportToCSV } from '../../utils/helpers'

const Usuarios = () => {
  const { user } = useAuth()
  const [usuarios, setUsuarios] = useState([])
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [showModal, setShowModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [editingUsuario, setEditingUsuario] = useState(null)
  const [viewingUsuario, setViewingUsuario] = useState(null)
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState(null)
  const fileInputRef = useRef(null)

  const { register, handleSubmit, reset, formState: { errors } } = useForm()

  useEffect(() => {
    fetchUsuarios()
    fetchRoles()
  }, [currentPage, perPage, searchTerm])

  const fetchUsuarios = async () => {
    try {
      setLoading(true)
      const response = await userService.getUsers({
        page: currentPage,
        per_page: perPage,
        search: searchTerm
      })
      
      if (response.success && response.data) {
        // response.data es el objeto paginado de Laravel: { data: [...], last_page: 1, total: X, ... }
        setUsuarios(response.data.data || [])
        setTotalPages(response.data.last_page || 1)
      } else {
        toast.error(response.message || 'Error al cargar usuarios')
        setUsuarios([])
        setTotalPages(1)
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Error de conexión'
      toast.error(errorMessage)
      console.error('Error al cargar usuarios:', error)
      setUsuarios([])
      setTotalPages(1)
    } finally {
      setLoading(false)
    }
  }

  const fetchRoles = async () => {
    try {
      const response = await roleService.getAllRoles()
      if (response.success) {
        setRoles(response.data || [])
      }
    } catch (error) {
      console.error('Error al cargar roles:', error)
    }
  }

  const handleCreate = () => {
    setEditingUsuario(null)
    reset({
      name: '',
      email: '',
      password: '',
      rol_id: '',
      activo: true
    })
    setShowModal(true)
  }

  const handleEdit = (usuario) => {
    setEditingUsuario(usuario)
    reset({
      name: usuario.name,
      email: usuario.email,
      rol_id: usuario.rol_id || usuario.rol?.id,
      activo: usuario.activo
    })
    setShowModal(true)
  }

  const handleView = async (usuario) => {
    try {
      const response = await userService.getUser(usuario.id)
      if (response.success) {
        setViewingUsuario(response.data)
        setShowViewModal(true)
      }
    } catch (error) {
      toast.error('Error al cargar el usuario')
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este usuario?')) return
    
    try {
      setLoading(true)
      const response = await userService.deleteUser(id)
      
      if (response.success) {
        toast.success(response.message || 'Usuario eliminado exitosamente')
        await fetchUsuarios()
      } else {
        toast.error(response.message || 'Error al eliminar el usuario')
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Error al eliminar el usuario'
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleToggleStatus = async (id) => {
    try {
      const response = await userService.toggleStatus(id)
      if (response.success) {
        toast.success(response.message || 'Estado actualizado exitosamente')
        await fetchUsuarios()
      } else {
        toast.error(response.message || 'Error al actualizar el estado')
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Error al actualizar el estado'
      toast.error(errorMessage)
    }
  }

  const onSubmit = async (data) => {
    try {
      setLoading(true)
      
      const datosBackend = {
        ...data,
        rol_id: parseInt(data.rol_id),
        activo: data.activo === 'true' || data.activo === true || data.activo === '1' || data.activo === 1
      }

      // Si es edición, no enviar password si está vacío
      if (editingUsuario && !data.password) {
        delete datosBackend.password
      }

      let response
      
      if (editingUsuario) {
        response = await userService.updateUser(editingUsuario.id, datosBackend)
      } else {
        if (!data.password) {
          toast.error('La contraseña es obligatoria para crear un nuevo usuario')
          return
        }
        response = await userService.createUser(datosBackend)
      }
      
      if (response.success) {
        toast.success(response.message || (editingUsuario ? 'Usuario actualizado exitosamente' : 'Usuario creado exitosamente'))
        setShowModal(false)
        setEditingUsuario(null)
        reset()
        await fetchUsuarios()
      } else {
        toast.error(response.message || 'Error al guardar el usuario')
        if (response.errors) {
          Object.keys(response.errors).forEach(key => {
            toast.error(`${key}: ${response.errors[key]}`)
          })
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Error al guardar el usuario'
      toast.error(errorMessage)
      if (error.response?.data?.errors) {
        Object.keys(error.response.data.errors).forEach(key => {
          toast.error(`${key}: ${error.response.data.errors[key]}`)
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleImport = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    if (!file.name.match(/\.(xlsx|xls|csv)$/i)) {
      toast.error('Por favor, selecciona un archivo Excel o CSV')
      return
    }

    setImporting(true)
    setImportResult(null)

    try {
      const response = await userService.importUsers(file)
      
      if (response.success) {
        setImportResult(response.data)
        toast.success(response.message || 'Usuarios importados exitosamente')
        await fetchUsuarios()
        setTimeout(() => {
          setShowImportModal(false)
          setImportResult(null)
          if (fileInputRef.current) {
            fileInputRef.current.value = ''
          }
        }, 3000)
      } else {
        toast.error(response.message || 'Error al importar usuarios')
        if (response.errors) {
          setImportResult({ errores: response.errors })
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Error al importar usuarios'
      toast.error(errorMessage)
    } finally {
      setImporting(false)
    }
  }

  const downloadTemplate = () => {
    // Crear template CSV
    const template = 'nombre,email,rol,codigo_docente\nJuan Pérez,juan.perez@example.com,docente,DOC001\nMaría González,maria.gonzalez@example.com,coordinador,\n'
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = 'template_usuarios.csv'
    link.click()
  }

  const handleExport = async () => {
    try {
      setLoading(true)
      const response = await userService.exportUsers({ search: searchTerm })
      
      if (response.success && response.data && Array.isArray(response.data)) {
        if (response.data.length === 0) {
          toast.error('No hay usuarios para exportar')
          return
        }
        
        const datosExportar = response.data.map(usuario => ({
          'Nombre': usuario.name || '',
          'Email': usuario.email || '',
          'Rol': usuario.rol?.nombre || 'Sin rol',
          'Estado': usuario.activo ? 'Activo' : 'Inactivo',
          'Creado': usuario.created_at ? new Date(usuario.created_at).toLocaleDateString('es-ES') : ''
        }))
        
        exportToCSV(datosExportar, `usuarios_${new Date().toISOString().split('T')[0]}.csv`)
        toast.success(`Se exportaron ${datosExportar.length} usuarios exitosamente`)
      } else {
        toast.error(response.message || 'Error al exportar usuarios: No se recibieron datos válidos')
        console.error('Error en exportación:', response)
      }
    } catch (error) {
      console.error('Error al exportar usuarios:', error)
      toast.error(error.response?.data?.message || error.message || 'Error al exportar usuarios')
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      key: 'name',
      label: 'Usuario',
      sortable: true,
      render: (row) => (
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-white font-semibold">
              {row.name?.charAt(0) || 'U'}
            </div>
          </div>
          <div>
            <div className="font-medium text-gray-900 dark:text-gray-100">{row.name}</div>
            <div className="text-sm text-gray-500">{row.email}</div>
          </div>
        </div>
      )
    },
    {
      key: 'rol',
      label: 'Rol',
      sortable: true,
      render: (row) => (
        <div className="flex items-center space-x-2">
          <Shield className="h-4 w-4 text-gray-400" />
          <span className="text-gray-900 dark:text-gray-100 capitalize">
            {row.rol?.nombre || 'Sin rol'}
          </span>
        </div>
      )
    },
    {
      key: 'activo',
      label: 'Estado',
      sortable: true,
      render: (row) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          row.activo 
            ? 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-400' 
            : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400'
        }`}>
          {row.activo ? (
            <>
              <CheckCircle className="h-3 w-3 mr-1" />
              Activo
            </>
          ) : (
            <>
              <XCircle className="h-3 w-3 mr-1" />
              Inactivo
            </>
          )}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (row) => (
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            icon={<Eye className="h-4 w-4" />}
            onClick={() => handleView(row)}
          />
          <Button
            variant="ghost"
            size="sm"
            icon={row.activo ? <XCircle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
            onClick={() => handleToggleStatus(row.id)}
            title={row.activo ? 'Desactivar' : 'Activar'}
          />
          <Button
            variant="ghost"
            size="sm"
            icon={<Edit2 className="h-4 w-4" />}
            onClick={() => handleEdit(row)}
          />
          {row.id !== user?.id && (
            <Button
              variant="ghost"
              size="sm"
              icon={<Trash2 className="h-4 w-4" />}
              onClick={() => handleDelete(row.id)}
            />
          )}
        </div>
      )
    }
  ]

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-4 mb-4 sm:mb-0">
          <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center shadow-glow">
            <Users className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold gradient-text">Gestión de Usuarios</h1>
            <p className="text-gray-600 dark:text-gray-400">Administra los usuarios del sistema</p>
          </div>
        </div>
        <div className="flex space-x-2">
          <Button
            variant="success"
            icon={<Upload className="h-5 w-5" />}
            onClick={() => setShowImportModal(true)}
          >
            Importar
          </Button>
          <Button
            variant="primary"
            icon={<Plus className="h-5 w-5" />}
            onClick={handleCreate}
          >
            Nuevo Usuario
          </Button>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
        <Card className="gradient" shadow="glow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">Total Usuarios</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{usuarios.length}</p>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-glow">
              <Users className="h-6 w-6 text-white" />
            </div>
          </div>
        </Card>
        
        <Card className="gradient" shadow="glow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">Usuarios Activos</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {usuarios.filter(u => u.activo).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-success-500 to-success-600 rounded-xl flex items-center justify-center shadow-glow">
              <CheckCircle className="h-6 w-6 text-white" />
            </div>
          </div>
        </Card>
        
        <Card className="gradient" shadow="glow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">Docentes</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {usuarios.filter(u => u.rol?.nombre === 'docente').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-accent-500 to-accent-600 rounded-xl flex items-center justify-center shadow-glow">
              <Shield className="h-6 w-6 text-white" />
            </div>
          </div>
        </Card>
        
        <Card className="gradient" shadow="glow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">Coordinadores</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {usuarios.filter(u => u.rol?.nombre === 'coordinador').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-info-500 to-info-600 rounded-xl flex items-center justify-center shadow-glow">
              <Shield className="h-6 w-6 text-white" />
            </div>
          </div>
        </Card>
      </div>

      {/* Tabla de Usuarios */}
      <Card className="gradient" shadow="glow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <h3 className="text-xl font-bold gradient-text mb-4 sm:mb-0">Lista de Usuarios</h3>
          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar usuarios..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full sm:w-64"
              />
            </div>
            <div className="flex space-x-2">
              <Button 
                variant="outline" 
                size="sm" 
                icon={<Upload className="h-4 w-4" />}
                onClick={() => setShowImportModal(true)}
              >
                Importar
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                icon={<Download className="h-4 w-4" />}
                onClick={handleExport}
                disabled={loading}
              >
                Exportar
              </Button>
            </div>
          </div>
        </div>
        
        <Table
          columns={columns}
          data={usuarios}
          loading={loading}
          pagination={{
            currentPage,
            totalPages,
            perPage,
            onPageChange: setCurrentPage,
            onPerPageChange: setPerPage
          }}
        />
      </Card>

      {/* Modal de Crear/Editar Usuario */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false)
          setEditingUsuario(null)
          reset()
        }}
        title={editingUsuario ? 'Editar Usuario' : 'Nuevo Usuario'}
        size="lg"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Input
              label="Nombre Completo *"
              placeholder="Ej: Juan Pérez"
              error={errors.name?.message}
              {...register('name', { required: 'El nombre es obligatorio' })}
            />
            
            <Input
              label="Email *"
              type="email"
              placeholder="Ej: juan@example.com"
              error={errors.email?.message}
              {...register('email', { 
                required: 'El email es obligatorio',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Email inválido'
                }
              })}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Rol *
              </label>
              <select
                {...register('rol_id', { required: 'El rol es obligatorio' })}
                className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Seleccionar rol...</option>
                {roles.map(r => (
                  <option key={r.id} value={r.id}>
                    {r.nombre} - {r.descripcion || ''}
                  </option>
                ))}
              </select>
              {errors.rol_id && (
                <p className="mt-1 text-sm text-error-600">{errors.rol_id.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Estado
              </label>
              <select
                {...register('activo', { 
                  setValueAs: (value) => value === 'true' || value === true || value === '1' || value === 1
                })}
                className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-primary-500"
              >
                <option value="true">Activo</option>
                <option value="false">Inactivo</option>
              </select>
            </div>

            {!editingUsuario && (
              <Input
                label="Contraseña *"
                type="password"
                placeholder="Mínimo 8 caracteres"
                error={errors.password?.message}
                {...register('password', { 
                  required: !editingUsuario && 'La contraseña es obligatoria',
                  minLength: { value: 8, message: 'La contraseña debe tener al menos 8 caracteres' }
                })}
              />
            )}

            {editingUsuario && (
              <Input
                label="Nueva Contraseña (opcional)"
                type="password"
                placeholder="Dejar vacío para mantener la actual"
                error={errors.password?.message}
                {...register('password', { 
                  minLength: { value: 8, message: 'La contraseña debe tener al menos 8 caracteres' }
                })}
              />
            )}
          </div>
          
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowModal(false)
                setEditingUsuario(null)
                reset()
              }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              icon={<Plus className="h-5 w-5" />}
            >
              {editingUsuario ? 'Actualizar' : 'Crear'} Usuario
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal de Importar Usuarios */}
      <Modal
        isOpen={showImportModal}
        onClose={() => {
          setShowImportModal(false)
          setImportResult(null)
          if (fileInputRef.current) {
            fileInputRef.current.value = ''
          }
        }}
        title="Importar Usuarios"
        size="lg"
      >
        <div className="space-y-6">
          <div className="p-4 bg-info-50 dark:bg-info-900/20 rounded-lg">
            <h4 className="font-semibold text-info-900 dark:text-info-100 mb-2">Formato del archivo</h4>
            <p className="text-sm text-info-700 dark:text-info-300 mb-2">
              El archivo debe ser Excel (.xlsx, .xls) o CSV con las siguientes columnas:
            </p>
            <ul className="text-sm text-info-700 dark:text-info-300 list-disc list-inside space-y-1">
              <li><strong>nombre</strong> o <strong>name</strong> (obligatorio)</li>
              <li><strong>email</strong> o <strong>correo</strong> (obligatorio)</li>
              <li><strong>rol</strong> (opcional, por defecto: docente)</li>
              <li><strong>codigo</strong> o <strong>código</strong> (opcional, para docentes)</li>
            </ul>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Seleccionar archivo
            </label>
            <div className="flex items-center space-x-4">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleImport}
                disabled={importing}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
              />
              <Button
                type="button"
                variant="outline"
                icon={<Download className="h-4 w-4" />}
                onClick={downloadTemplate}
              >
                Template
              </Button>
            </div>
          </div>

          {importing && (
            <div className="text-center py-4">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="mt-2 text-sm text-gray-600">Importando usuarios...</p>
            </div>
          )}

          {importResult && (
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Resultado de la importación</h4>
              <div className="space-y-2">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong className="text-success-600">Creados:</strong> {importResult.creados || 0}
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong className="text-info-600">Actualizados:</strong> {importResult.actualizados || 0}
                </p>
                {importResult.errores && importResult.errores.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-semibold text-error-600 mb-2">Errores:</p>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {importResult.errores.map((error, index) => (
                        <p key={index} className="text-xs text-error-600">{error}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Modal de Ver Usuario */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Detalles del Usuario"
        size="lg"
      >
        {viewingUsuario && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nombre
                </label>
                <p className="text-gray-900 dark:text-gray-100">{viewingUsuario.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email
                </label>
                <p className="text-gray-900 dark:text-gray-100">{viewingUsuario.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Rol
                </label>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-400 capitalize">
                  {viewingUsuario.rol?.nombre || 'Sin rol'}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Estado
                </label>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  viewingUsuario.activo 
                    ? 'bg-success-100 text-success-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {viewingUsuario.activo ? 'Activo' : 'Inactivo'}
                </span>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default Usuarios

